import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Loader2, Send, FileText, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ChamadoAditivo() {
  const nav = useNavigate();
  const [empresa, setEmpresa] = useState("");
  const [catalogo, setCatalogo] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/portal/me").then((r) => {
      const c = r.data?.cliente;
      setEmpresa(c?.nome || c?.nome_fantasia || "");
    });
    api.get("/portal/catalogo-equipamentos")
      .then((r) => setCatalogo(r.data?.items || []))
      .catch(() => toast.error("Falha ao carregar catálogo"));
  }, []);

  const toggle = (id) => {
    const novo = new Set(sel);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    setSel(novo);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (sel.size === 0) { toast.error("Selecione ao menos 1 modelo"); return; }
    if (!obs.trim()) { toast.error("Informe a quantidade desejada / detalhes nas observações"); return; }
    setBusy(true);
    try {
      const modelos_selecionados = Array.from(sel)
        .map((id) => catalogo.find((c) => c.id === id))
        .filter(Boolean)
        .map((m) => `${m.nome} (${m.fabricante})`)
        .join("; ");
      const descricao = `MODELOS SOLICITADOS: ${modelos_selecionados}\n\nOBSERVAÇÕES: ${obs.trim()}`;
      await api.post("/portal/tickets", {
        tipo: "aditivo",
        descricao,
        modelo_ids: Array.from(sel),
      });
      toast.success("Solicitação de aditivo enviada");
      nav("/cliente/chamados");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="chamado-aditivo">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav("/cliente/home")} data-testid="aditivo-voltar">
          <ArrowLeft size={14} className="mr-1" /> Voltar
        </Button>
        <h1 className="text-2xl font-display font-bold" style={{ color: "#0f1923" }}>
          Abertura de ticket — Aditivo de frota
        </h1>
      </div>

      <Card className="p-4 bg-orange-50/50 border-orange-200 text-sm" data-testid="aditivo-alerta">
        <strong>Importante:</strong> um aditivo altera as condições do seu contrato atual. Nossa
        equipe comercial entrará em contato para formalizar a inclusão dos equipamentos
        selecionados.
      </Card>

      <form onSubmit={submit} className="space-y-5">
        <Card className="p-5 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Empresa</Label>
            <Input value={empresa} disabled data-testid="aditivo-empresa" className="mt-1.5 bg-gray-50" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">
              Modelos disponíveis ({catalogo.length})
            </Label>
            {catalogo.length === 0 ? (
              <div className="border border-dashed rounded p-6 text-center text-sm text-gray-500">
                <Loader2 className="animate-spin mx-auto mb-2" size={18} />
                Carregando catálogo...
              </div>
            ) : (
              <div className="border rounded max-h-80 overflow-y-auto divide-y" data-testid="aditivo-catalogo">
                {catalogo.map((m) => (
                  <label
                    key={m.id}
                    className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50 ${sel.has(m.id) ? "bg-orange-50" : ""}`}
                    data-testid={`aditivo-modelo-${m.id}`}
                  >
                    <Checkbox checked={sel.has(m.id)} onCheckedChange={() => toggle(m.id)} className="mt-0.5" />
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded bg-orange-100 flex items-center justify-center text-orange-600 shrink-0">
                        <FileText size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm" style={{ color: "#0f1923" }}>{m.nome}</div>
                        <div className="text-xs text-gray-500">
                          {m.fabricante || "—"}
                          {m.categoria ? ` · ${m.categoria}` : ""}
                        </div>
                        {m.especificacao && (
                          <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{m.especificacao}</div>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <div className="mt-2 text-xs text-gray-500">{sel.size} modelo(s) selecionado(s)</div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">
              Observações (incluir quantidade desejada por modelo)
            </Label>
            <Textarea
              rows={4} placeholder="Ex: 3 unidades do modelo A; 2 unidades do modelo B; prazo desejado: imediato"
              value={obs} onChange={(e) => setObs(e.target.value)}
              data-testid="aditivo-observacoes"
              className="mt-1.5"
            />
          </div>
        </Card>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={busy || sel.size === 0}
            data-testid="aditivo-submit"
            className="text-white"
            style={{ background: "#ff6b23" }}
          >
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}
            Abrir ticket
          </Button>
        </div>
      </form>
    </div>
  );
}
